/**
 * /dashboard/hiring/requisitions/$id/pipeline — Pipeline Kanban board
 * (Phase H.8).
 *
 * GET /hiring/requisitions/{id}/pipeline is HR-admin OR the owning
 * hiring manager (server-resolved; a non-owner non-HR caller gets a
 * neutral 404). The response is the FULL board.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/hiring/requisitions/[id]/
 * pipeline/page.tsx. Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { useMemo } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil } from "lucide-react";
import {
  useRequisitionPipeline,
  hiringKeys,
} from "@/hooks/use-hiring";
import { hiringApi, toApiError } from "@/lib/api";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import { HiringFeatureGate } from "@/components/hiring/hiring-feature-gate";
import { ApplicationCard } from "@/components/hiring/application-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hiring/requisitions/$id/pipeline",
)({
  component: PipelineRoute,
});

function PipelineBoard({ id }: { readonly id: number }) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const pipe = useRequisitionPipeline(id);
  // staleTime: 0 ensures a fresh fetch fires on every pipeline-page
  // mount, bypassing the 5-min localStorage cache. Without this the
  // name map stays empty when the import mutation's invalidation runs
  // on the Candidates page and then page.goto causes a full SPA reload
  // that rehydrates the pre-import stale cache — candidate names never
  // resolve and the card renders "Candidate #<id>".
  const cands = useQuery({
    queryKey: hiringKeys.candidates(),
    queryFn: ({ signal }) => hiringApi.listCandidates(undefined, signal),
    staleTime: 0,
  });

  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of cands.data?.candidates ?? []) {
      m.set(
        c.id,
        `${c.first_name} ${c.last_name}`.trim() || c.email,
      );
    }
    return m;
  }, [cands.data]);

  if (pipe.isLoading) {
    return (
      <HiringModuleShell title="Pipeline">
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64 w-72 shrink-0" />
          ))}
        </div>
      </HiringModuleShell>
    );
  }

  if (pipe.isError) {
    const e = toApiError(pipe.error);
    const notAvailable =
      e.error === "not_found" || e.error === "forbidden";
    return (
      <HiringModuleShell title="Pipeline">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {notAvailable
                ? "This pipeline isn't available. You need to be an HR admin or the requisition's hiring manager."
                : e.message}
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to Requisitions
              </Link>
            </Button>
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  const columns = pipe.data?.pipeline ?? [];
  const totalApps = columns.reduce(
    (n, c) => n + c.applications.length,
    0,
  );

  return (
    <HiringModuleShell
      title="Pipeline"
      description="Move candidates across stages. Rejecting needs a reason; the hire happens from a candidate's profile."
      actions={
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/hiring/requisitions/$id"
              params={{tenantSlug, id: String(id)}}
            >
              <Pencil className="h-4 w-4" />
              Edit requisition
            </Link>
          </Button>
          <Button asChild variant="ghost" className="gap-1.5">
            <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              All requisitions
            </Link>
          </Button>
        </div>
      }
    >
      {totalApps === 0 && (
        <Alert className="mb-4">
          <AlertTitle>No candidates yet</AlertTitle>
          <AlertDescription>
            Candidates appear here when they apply via the careers page
            or when you import/attach them to this requisition from the
            Candidates page.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => (
          <div
            key={col.stage.id}
            className="flex w-72 shrink-0 flex-col rounded-xl bg-muted/40 p-3"
          >
            <div className="mb-3 flex items-center justify-between px-1">
              <h3 className="text-sm font-semibold text-gray-900">
                {col.stage.name}
              </h3>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {col.applications.length}
              </span>
            </div>
            <div className="flex-1 space-y-2">
              {col.applications.length === 0 ? (
                <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                  No candidates in this stage.
                </p>
              ) : (
                col.applications.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    stages={columns.map((c) => c.stage)}
                    candidateName={
                      nameById.get(app.candidate_id) ??
                      `Candidate #${app.candidate_id}`
                    }
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </HiringModuleShell>
  );
}

function PipelineRoute() {
  const { id } = Route.useParams();
  const numId = Number(id);

  return (
    <HiringFeatureGate>
      {Number.isFinite(numId) && numId > 0 ? (
        <PipelineBoard id={numId} />
      ) : (
        <HiringModuleShell title="Pipeline">
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-sm text-muted-foreground">
                Invalid requisition.
              </p>
            </CardContent>
          </Card>
        </HiringModuleShell>
      )}
    </HiringFeatureGate>
  );
}
